import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ArrowRight } from 'lucide-react';
import { useSnapshot } from '../../context/SnapshotContext';

const LINKS = [
    { href: '#what-we-build', label: 'Services', testid: 'nav-link-services' },
    { href: '#featured-systems', label: 'Work', testid: 'nav-link-work' },
    { href: '#how-we-work', label: 'Method', testid: 'nav-link-process' },
    { href: '#why-us', label: 'Why us', testid: 'nav-link-why' },
];

export function Nav() {
    const { openSnapshot } = useSnapshot();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        document.documentElement.style.overflow = mobileOpen ? 'hidden' : '';
        return () => { document.documentElement.style.overflow = ''; };
    }, [mobileOpen]);

    return (
        <>
            <motion.nav
                data-testid="site-nav"
                initial={{ y: -16, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                className="fixed top-0 inset-x-0 z-50 transition-all duration-500"
                style={{
                    background: scrolled ? 'rgba(5, 10, 21, 0.72)' : 'rgba(5, 10, 21, 0.0)',
                    backdropFilter: scrolled ? 'blur(20px) saturate(140%)' : 'none',
                    WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(140%)' : 'none',
                    borderBottom: scrolled ? '1px solid var(--border-mid)' : '1px solid transparent',
                }}
            >
                <div className="shell h-[68px] flex items-center justify-between">
                    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5">
                        <Logomark />
                        <span className="font-display font-semibold text-[19px] tracking-[-0.02em] text-white">
                            ClearAdvance
                        </span>
                    </Link>

                    <div className="hidden md:flex items-center gap-9">
                        {LINKS.map((l) => (
                            <a
                                key={l.href}
                                href={l.href}
                                data-testid={l.testid}
                                className="text-[14px] text-slate-300 hover:text-white transition-colors duration-300 font-medium"
                            >
                                {l.label}
                            </a>
                        ))}
                    </div>

                    <div className="flex items-center gap-3">
                        <button
                            type="button"
                            data-testid="nav-cta-button"
                            onClick={openSnapshot}
                            className="!hidden md:!inline-flex btn btn-primary !py-2.5 !px-4 !text-[13px]"
                        >
                            Find Where You're Losing Clients
                            <ArrowRight className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            data-testid="nav-mobile-toggle"
                            aria-label="Open menu"
                            onClick={() => setMobileOpen(true)}
                            className="md:hidden grid place-items-center w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] text-white"
                        >
                            <Menu className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </motion.nav>

            <AnimatePresence>
                {mobileOpen && (
                    <motion.div
                        data-testid="mobile-menu"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="fixed inset-0 z-[60] md:hidden"
                        style={{ background: 'rgba(2, 5, 12, 0.94)', backdropFilter: 'blur(20px)' }}
                    >
                        <div className="flex items-center justify-between h-[68px] px-5">
                            <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2.5">
                                <Logomark />
                                <span className="font-display font-semibold text-[19px] text-white">ClearAdvance</span>
                            </Link>
                            <button
                                type="button"
                                aria-label="Close menu"
                                data-testid="nav-mobile-close"
                                onClick={() => setMobileOpen(false)}
                                className="grid place-items-center w-10 h-10 rounded-lg border border-white/10 bg-white/[0.03] text-white"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="px-5 pt-4 flex flex-col gap-1">
                            {LINKS.map((l, i) => (
                                <motion.a
                                    key={l.href}
                                    href={l.href}
                                    initial={{ opacity: 0, y: 12 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.06 + i * 0.06, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                                    onClick={() => setMobileOpen(false)}
                                    className="font-display text-[40px] leading-[1.05] tracking-[-0.03em] text-white py-3 border-b border-white/5"
                                >
                                    {l.label}
                                </motion.a>
                            ))}
                            <button
                                type="button"
                                data-testid="mobile-cta-button"
                                onClick={() => { setMobileOpen(false); openSnapshot(); }}
                                className="mt-7 btn btn-primary justify-center w-full !text-[14px]"
                            >
                                Find Where You're Losing Clients
                                <ArrowRight className="w-4 h-4" />
                            </button>
                            <p className="mt-3 font-mono text-[10px] tracking-[0.22em] uppercase text-slate-500 text-center">
                                2-min diagnostic · no call required
                            </p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
}

function Logomark() {
    return (
        <div className="relative w-7 h-7 grid place-items-center">
            <div
                className="absolute inset-0 rounded-md"
                style={{
                    background: 'linear-gradient(135deg, #06B6D4 0%, #3B82F6 50%, #8B5CF6 100%)',
                    boxShadow: '0 0 22px -4px rgba(6, 182, 212, 0.45)',
                }}
            />
            <svg viewBox="0 0 24 24" className="relative w-3.5 h-3.5 text-white">
                <path d="M5 12 L11 12 M11 6 L19 6 M11 12 L19 12 M11 18 L19 18" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" />
                <circle cx="5" cy="12" r="1.5" fill="currentColor" />
            </svg>
        </div>
    );
}
